import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface VeinedNeonicSphereProps {
  quaternion?: THREE.Quaternion;
  scale?: number;
  radius?: number;
  segments?: number;
}

const VeinedNeonicSphere: React.FC<VeinedNeonicSphereProps> = ({
  quaternion,
  scale = 1,
  radius = 2,
  segments = 64
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const timeRef = useRef(0);
//   const { camera } = useThree();

  // Fonctions de bruit Simplex pour générer des motifs organiques
  const glslNoise = `
    vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }
    
    vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }
    
    vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
    }
    
    vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
    }
    
    float snoise(vec3 v) {
        const vec2 C = vec2(1.0/6.0, 1.0/3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        vec3 i  = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        i = mod289(i);
        vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0)) +
            i.y + vec4(0.0, i1.y, i2.y, 1.0)) +
            i.x + vec4(0.0, i1.x, i2.x, 1.0));
        float n_ = 0.142857142857;
        vec3  ns = n_ * D.wyz - D.xzx;
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        vec4 x = x_ * ns.x + ns.yyyy;
        vec4 y = y_ * ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
        vec3 p0 = vec3(a0.xy,h.x);
        vec3 p1 = vec3(a0.zw,h.y);
        vec3 p2 = vec3(a1.xy,h.z);
        vec3 p3 = vec3(a1.zw,h.w);
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
    }
    
  `;

  const vertexShader = `
    varying vec3 vNormal;
    varying vec3 vPosition;
    
    void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const fragmentShader = `
    uniform float time;
//     uniform vec3 cameraPosition;
    varying vec3 vNormal;
    varying vec3 vPosition;
    ${glslNoise}
    
    void main() {
        // Génération du bruit pour les veines
        vec3 p = vPosition * 5.2 + time * 0.1;
        float combinedNoise = snoise(p * 2.0);
        
        // Calcul de l'intensité des veines (plus le bruit est proche de 0, plus la veine est visible)
        float veinStrength = 1.0 - smoothstep(0.0, 0.04, abs(combinedNoise));
        veinStrength = pow(veinStrength, 1.5);
        
        // Effet de caustique (lumière qui émane du core)
        vec3 corePosition = vec3(0.0, 0.0, 0.0);
        float distFromCore = length(vPosition - corePosition);
        float causticEffect = 1.0 - smoothstep(0.0, 0.5, distFromCore);
        causticEffect = pow(causticEffect, 2.0);
        
        // Couleurs de base et des veines
        vec4 baseColor = vec4(0.1, 0.0, 0.01, 0.05);
        vec4 veinColor = vec4(1.0, 0.1, 0.7, 1.0);
        
        // Mélange entre couleur de base et couleur des veines
        vec4 finalColor = mix(baseColor, veinColor, veinStrength);
        
        // Application de l'effet caustique
        float causticIntensity = 3.5;
        finalColor.rgb *= (1.0 + causticEffect * causticIntensity);
        finalColor.a += causticEffect * 0.8;
        
        // Renforcement des veines
        finalColor.rgb += veinColor.rgb * veinStrength * 1.8;
        
        // Effet Fresnel pour les bords brillants
        vec3 viewDirection = normalize(cameraPosition - vPosition);
        float fresnel = pow(1.0 - abs(dot(vNormal, viewDirection)), 3.0);
        finalColor.rgb += vec3(0.2, 0.5, 0.8) * fresnel * 0.5;
        
        gl_FragColor = finalColor;
    }
  `;

  const { geometry, material } = useMemo(() => {
    const sphereGeometry = new THREE.SphereGeometry(radius, segments, segments);
    
    const sphereMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        // cameraPosition: { value: new THREE.Vector3() }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    return {
      geometry: sphereGeometry,
      material: sphereMaterial
    };
  }, [radius, segments]);

  // Animation dans useFrame
  useFrame((state, delta) => {
    timeRef.current += delta;

    if(state)

    // Mettre à jour le temps dans le shader
    if (material && (material as THREE.ShaderMaterial).uniforms) {
      (material as THREE.ShaderMaterial).uniforms.time.value = timeRef.current;
      // (material as THREE.ShaderMaterial).uniforms.cameraPosition.value.copy(camera.position);
    }

    // Rotation lente de la sphère
    if (meshRef.current) {
    //   meshRef.current.rotation.x += 0.01;
    //   meshRef.current.rotation.y += 0.02;
    }
  });

  return (
    <group scale={scale} quaternion={quaternion}>
      <mesh ref={meshRef} geometry={geometry} material={material} frustumCulled={false} />
    </group>
  );
};

export default VeinedNeonicSphere;

